import type { FC, PointerEvent, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'

interface TiltProps {
    children: ReactNode
    className?: string
    max?: number
    glare?: boolean
}

/* Tilts its content in 3D toward the mouse, with a soft glare. Mouse only, and off when the visitor asks for less motion. */
export const Tilt: FC<TiltProps> = ({
    children,
    className,
    max = 7,
    glare = true
}): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const frame = useRef(0)
    const enabled = useRef(false)

    useEffect(() => {
        const media = window.matchMedia('(hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)')
        const update = () => {
            enabled.current = media.matches
        }

        update()
        media.addEventListener('change', update)

        return () => {
            media.removeEventListener('change', update)
            cancelAnimationFrame(frame.current)
        }
    }, [])

    const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
        const element = ref.current

        if (!enabled.current || event.pointerType !== 'mouse' || !element) return

        const rect = element.getBoundingClientRect()
        const x = (event.clientX - rect.left) / rect.width
        const y = (event.clientY - rect.top) / rect.height

        cancelAnimationFrame(frame.current)

        frame.current = requestAnimationFrame(() => {
            element.style.setProperty('--tilt-x', `${((0.5 - y) * max * 2).toFixed(2)}deg`)
            element.style.setProperty('--tilt-y', `${((x - 0.5) * max * 2).toFixed(2)}deg`)
            element.style.setProperty('--glare-x', `${(x * 100).toFixed(1)}%`)
            element.style.setProperty('--glare-y', `${(y * 100).toFixed(1)}%`)
            element.dataset.tilting = 'true'
        })
    }

    const onPointerLeave = () => {
        const element = ref.current

        cancelAnimationFrame(frame.current)

        if (!element) return

        element.style.setProperty('--tilt-x', '0deg')
        element.style.setProperty('--tilt-y', '0deg')
        delete element.dataset.tilting
    }

    return (
        <div ref={ref} onPointerMove={onPointerMove} onPointerLeave={onPointerLeave} className={cn('tilt-3d', className)}>
            {children}
            {glare && <span aria-hidden='true' className='tilt-glare' />}
        </div>
    )
}
